import { X } from 'lucide-react';
import { useState } from 'react';
import { EmojiPicker } from '../../../components/EmojiPicker';
import {
  DailyTaskActionType,
  DAILY_TASK_ACTION_TYPE_LABELS,
  DailyTaskCategory,
  DAILY_TASK_CATEGORY_LABELS,
  DailyTaskColorVariant,
  DAILY_TASK_COLOR_VARIANT_LABELS,
  DailyTaskDeepLinkTarget,
  DAILY_TASK_DEEP_LINK_TARGET_LABELS,
  DayKind,
  DAY_KIND_LABELS,
} from '../services/dailyTaskTemplateTypes';
import type { DailyTaskTemplateFirestore } from '../services/dailyTaskTemplateTypes';

type FormData = Omit<DailyTaskTemplateFirestore, 'id' | 'createdAt' | 'updatedAt'>;

interface DailyTaskTemplateFormProps {
  initialData?: DailyTaskTemplateFirestore;
  onSubmit: (data: FormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  onChange?: (data: FormData) => void;
}

const DEFAULT_FORM_DATA: FormData = {
  title: '',
  emoji: '',
  isPublished: false,
  colorVariant: DailyTaskColorVariant.BLUE,
  category: DailyTaskCategory.ERP_PRACTICE,
  actionType: DailyTaskActionType.MANUAL,
  totalSteps: 1,
  energy: 5,
  textTemplatingPrompt: '',
  ocdSubtypeTags: [],
  journeyChapterRelevance: [],
  dayKindTags: [],
};

const toFormData = (template: DailyTaskTemplateFirestore): FormData => {
  const { id: _id, createdAt: _ca, updatedAt: _ua, ...data } = template;
  if (data.actionType === DailyTaskActionType.DEEP_LINK) {
    return {
      ...data,
      deepLinkTarget: DailyTaskDeepLinkTarget.TRIGGER_LOG,
      deepLinkParams: undefined,
    };
  }
  return data;
};

export const DailyTaskTemplateForm = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  onChange,
}: DailyTaskTemplateFormProps) => {
  const [form, setForm] = useState<FormData>(
    initialData ? toFormData(initialData) : DEFAULT_FORM_DATA,
  );

  const [tagInput, setTagInput] = useState('');
  const [chapterInput, setChapterInput] = useState('');

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      onChange?.(next);
      return next;
    });
  };

  const handleAddTag = (
    input: string,
    setInput: (v: string) => void,
  ) => {
    const trimmed = input.trim().toLowerCase().replace(/\s+/g, '_');
    if (!trimmed || form.ocdSubtypeTags.includes(trimmed)) return;
    updateField('ocdSubtypeTags', [...form.ocdSubtypeTags, trimmed]);
    setInput('');
  };

  const handleRemoveTag = (tag: string) => {
    updateField('ocdSubtypeTags', form.ocdSubtypeTags.filter((t) => t !== tag));
  };

  const handleAddChapter = () => {
    const num = parseInt(chapterInput, 10);
    if (isNaN(num) || num < 1 || form.journeyChapterRelevance.includes(num)) return;
    updateField('journeyChapterRelevance', [...form.journeyChapterRelevance, num].sort((a, b) => a - b));
    setChapterInput('');
  };

  const handleRemoveChapter = (chapter: number) => {
    updateField('journeyChapterRelevance', form.journeyChapterRelevance.filter((c) => c !== chapter));
  };

  const handleToggleDayKind = (dayKind: DayKind) => {
    const current = form.dayKindTags;
    if (current.includes(dayKind)) {
      updateField('dayKindTags', current.filter((k) => k !== dayKind));
    } else {
      updateField('dayKindTags', [...current, dayKind]);
    }
  };

  const needsAiGuidance = !form.title || !form.emoji;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (needsAiGuidance && !form.textTemplatingPrompt.trim()) return;
    onSubmit(form);
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">

      {/* Text Templating Prompt */}
      <div>
        <label htmlFor="textTemplatingPrompt" className={labelClass}>
          AI Templating Prompt {needsAiGuidance && <span className="text-red-500">*</span>}
        </label>
        <textarea
          id="textTemplatingPrompt"
          value={form.textTemplatingPrompt}
          onChange={(e) => updateField('textTemplatingPrompt', e.target.value)}
          className={`${inputClass} ${needsAiGuidance && !form.textTemplatingPrompt.trim() ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
          placeholder="Instructions for AI to personalize the title/description based on user's OCD profile"
          rows={3}
          required={needsAiGuidance}
        />
        {needsAiGuidance && (
          <p className="text-xs text-amber-600 mt-1">Required when title or emoji is empty (AI needs guidance to generate them)</p>
        )}
      </div>

      {/* Title */}
      <div>
        <label htmlFor="title" className={labelClass}>Title</label>
        <input
          type="text"
          id="title"
          value={form.title}
          onChange={(e) => updateField('title', e.target.value)}
          className={inputClass}
          placeholder="e.g. Resist a compulsion for 5 minutes"
        />
        <p className="text-xs text-gray-400 mt-1">Leave empty to let AI generate from the templating prompt</p>
      </div>

      {/* Emoji + Color Variant */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <EmojiPicker
            id="emoji"
            label="Emoji"
            value={form.emoji}
            onChange={(emoji) => updateField('emoji', emoji ?? '')}
            placeholder="💪"
            helperText="Leave empty for AI"
          />
        </div>
        <div>
          <label htmlFor="colorVariant" className={labelClass}>Color Variant</label>
          <select
            id="colorVariant"
            value={form.colorVariant}
            onChange={(e) => updateField('colorVariant', e.target.value as DailyTaskColorVariant)}
            className={inputClass}
          >
            {Object.entries(DAILY_TASK_COLOR_VARIANT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Category */}
      <div>
        <label htmlFor="category" className={labelClass}>Category *</label>
        <select
          id="category"
          value={form.category}
          onChange={(e) => updateField('category', e.target.value as DailyTaskCategory)}
          className={inputClass}
        >
          {Object.entries(DAILY_TASK_CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* Action Type */}
      <div>
        <label className={labelClass}>Action Type</label>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {Object.entries(DAILY_TASK_ACTION_TYPE_LABELS).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="actionType"
                value={value}
                checked={form.actionType === value}
                onChange={() => {
                  if (value === DailyTaskActionType.MANUAL) {
                    updateField('actionType', DailyTaskActionType.MANUAL);
                    updateField('deepLinkTarget', undefined);
                    updateField('deepLinkParams', undefined);
                  } else {
                    setForm((prev) => {
                      const next: FormData = {
                        ...prev,
                        actionType: DailyTaskActionType.DEEP_LINK,
                        deepLinkTarget: DailyTaskDeepLinkTarget.TRIGGER_LOG,
                        deepLinkParams: undefined,
                      };
                      onChange?.(next);
                      return next;
                    });
                  }
                }}
                className="text-blue-600"
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Deep Link Target (conditional) */}
      {form.actionType === DailyTaskActionType.DEEP_LINK && (
        <div className="pl-4 border-l-2 border-blue-200">
          <p className="text-sm text-gray-700">
            Deep link target:{' '}
            <span className="font-medium">{DAILY_TASK_DEEP_LINK_TARGET_LABELS[DailyTaskDeepLinkTarget.TRIGGER_LOG]}</span>
          </p>
        </div>
      )}

      {/* Energy + Total Steps */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="energy" className={labelClass}>Energy Reward *</label>
          <input
            type="number"
            id="energy"
            value={form.energy}
            onChange={(e) => updateField('energy', parseInt(e.target.value, 10) || 1)}
            className={inputClass}
            min={1}
            required
          />
        </div>
        <div>
          <label htmlFor="totalSteps" className={labelClass}>Total Steps *</label>
          <input
            type="number"
            id="totalSteps"
            value={form.totalSteps}
            onChange={(e) => updateField('totalSteps', parseInt(e.target.value, 10) || 1)}
            className={inputClass}
            min={1}
            required
          />
        </div>
      </div>

      {/* Day Kind Tags */}
      <div>
        <label className={labelClass}>Day Kind Tags</label>
        <p className="text-xs text-gray-500 mb-2">Leave empty for all day kinds</p>
        <div className="flex flex-wrap gap-2">
          {(Object.entries(DAY_KIND_LABELS) as [DayKind, string][]).map(([value, label]) => {
            const isSelected = form.dayKindTags.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => handleToggleDayKind(value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${isSelected
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* OCD Subtype Tags */}
      <div>
        <label className={labelClass}>OCD Subtype Tags</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag(tagInput, setTagInput);
              }
            }}
            className={`${inputClass} flex-1`}
            placeholder="e.g. contamination"
          />
          <button
            type="button"
            onClick={() => handleAddTag(tagInput, setTagInput)}
            className="px-3 py-2 bg-purple-100 text-purple-700 rounded-md text-sm hover:bg-purple-200"
          >
            Add
          </button>
        </div>
        {form.ocdSubtypeTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {form.ocdSubtypeTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs"
              >
                {tag}
                <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-purple-900">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Journey Chapter Relevance */}
      <div>
        <label className={labelClass}>Journey Chapter Relevance</label>
        <p className="text-xs text-gray-500 mb-2">Leave empty for all chapters</p>
        <div className="flex gap-2 mb-2">
          <input
            type="number"
            value={chapterInput}
            onChange={(e) => setChapterInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddChapter();
              }
            }}
            className={`${inputClass} w-24`}
            placeholder="Ch #"
            min={1}
          />
          <button
            type="button"
            onClick={handleAddChapter}
            className="px-3 py-2 bg-emerald-100 text-emerald-700 rounded-md text-sm hover:bg-emerald-200"
          >
            Add
          </button>
        </div>
        {form.journeyChapterRelevance.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {form.journeyChapterRelevance.map((ch) => (
              <span
                key={ch}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs"
              >
                Ch {ch}
                <button type="button" onClick={() => handleRemoveChapter(ch)} className="hover:text-emerald-900">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Is Published */}
      <div className="flex items-center gap-3 pt-2">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={form.isPublished}
            onChange={(e) => updateField('isPublished', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
        </label>
        <span className="text-sm font-medium text-gray-700">Published</span>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Template' : 'Create Template'}
        </button>
      </div>
    </form>
  );
};
